import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Input,
  InputNumber,
  Switch,
  Button,
  Select,
  Divider,
  Space,
  message,
  Tabs,
  Alert,
  Tooltip,
  Modal,
  List,
  Tag,
  Progress
} from 'antd';
import {
  SettingOutlined,
  SaveOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  FireOutlined,
  SoundOutlined,
  ExperimentOutlined
} from '@ant-design/icons';
import { environmentService } from '../../../services/environmentService';

const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

interface SystemConfigurationProps {
  onRefresh?: () => void;
}

interface ConfigData {
  airQuality: {
    enabled: boolean;
    monitoringInterval: number;
    alertThresholds: {
      aqi: { warning: number; critical: number };
      temperature: { min: number; max: number };
      humidity: { min: number; max: number };
      co2: { warning: number; critical: number };
    };
    autoVentilation: boolean;
    ventilationSchedule: string[];
  };
  lighting: {
    enabled: boolean;
    autoMode: boolean;
    brightnessRange: { min: number; max: number };
    schedule: {
      workdays: { start: string; end: string };
      weekends: { start: string; end: string };
    };
    motionSensor: boolean;
    energySaving: boolean;
  };
  noise: {
    enabled: boolean;
    monitoringInterval: number;
    alertThresholds: {
      warning: number;
      critical: number;
    };
    autoControl: boolean;
    quietHours: { start: string; end: string };
  };
  energy: {
    enabled: boolean;
    monitoringInterval: number;
    optimizationMode: string;
    alertThresholds: {
      dailyLimit: number;
      monthlyLimit: number;
      efficiencyMin: number;
    };
    autoOptimization: boolean;
    peakHours: string[];
  };
  system: {
    dataRetention: number;
    backupInterval: number;
    alertNotifications: boolean;
    emailNotifications: boolean;
    smsNotifications: boolean;
    logLevel: string;
    maintenanceMode: boolean;
  };
}

const SystemConfiguration: React.FC<SystemConfigurationProps> = ({ onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<ConfigData | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [form] = Form.useForm();

  // 載入系統配置
  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const data = await environmentService.getSystemConfig();
      setConfig(data);
      form.setFieldsValue(data);
      setHasChanges(false);
    } catch (error) {
      console.error('載入系統配置失敗:', error);
      message.error('載入系統配置失敗');
    } finally {
      setLoading(false);
    }
  };

  // 保存配置
  const saveConfiguration = async (values: any) => {
    try {
      setSaving(true);
      await environmentService.updateSystemConfig('default', values);
      message.success('系統配置保存成功');
      setConfig(values);
      setHasChanges(false);
      onRefresh?.();
    } catch (error) {
      console.error('保存系統配置失敗:', error);
      message.error('保存系統配置失敗');
    } finally {
      setSaving(false);
    }
  };

  // 重置配置
  const resetConfiguration = async () => {
    try {
      setSaving(true);
      // 使用默認配置
      const defaultConfig = {
        temperature: { min: 18, max: 26, enabled: true },
        humidity: { min: 40, max: 70, enabled: true },
        airQuality: { pm25Max: 35, co2Max: 1000, enabled: true },
        lighting: { minLux: 300, maxLux: 1000, enabled: true },
        noise: { maxLevel: 55, enabled: true },
        energy: { dailyLimit: 1000, monthlyLimit: 30000, enabled: true }
      };
      await environmentService.updateSystemConfig('default', defaultConfig);
      message.success('系統配置已重置為默認值');
      await loadConfiguration();
      setResetModalVisible(false);
      onRefresh?.();
    } catch (error) {
      console.error('重置系統配置失敗:', error);
      message.error('重置系統配置失敗');
    } finally {
      setSaving(false);
    }
  };

  // 表單值變化處理
  const handleFormChange = () => {
    setHasChanges(true);
  };

  // 獲取配置狀態
  const getConfigStatus = () => {
    if (!config) return null;

    const modules = [
      { name: '空氣品質', enabled: config.airQuality.enabled, key: 'airQuality' },
      { name: '智能照明', enabled: config.lighting.enabled, key: 'lighting' },
      { name: '噪音監控', enabled: config.noise.enabled, key: 'noise' },
      { name: '能源管理', enabled: config.energy.enabled, key: 'energy' }
    ];

    const enabledCount = modules.filter(m => m.enabled).length;
    const totalCount = modules.length;

    return {
      modules,
      enabledCount,
      totalCount,
      percentage: (enabledCount / totalCount) * 100
    };
  };

  useEffect(() => {
    loadConfiguration();
  }, []);

  const configStatus = getConfigStatus();

  return (
    <div className="system-configuration">
      {/* 配置狀態概覽 */}
      {configStatus && (
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col span={6}>
              <div style={{ textAlign: 'center' }}>
                <Progress
                  type="circle"
                  percent={configStatus.percentage}
                  format={() => `${configStatus.enabledCount}/${configStatus.totalCount}`}
                  strokeColor="#52c41a"
                  size={80}
                />
                <div style={{ marginTop: 8, fontSize: 14, fontWeight: 'bold' }}>
                  模組啟用狀態
                </div>
              </div>
            </Col>
            <Col span={18}>
              <Row gutter={[16, 8]}>
                {configStatus.modules.map(module => (
                  <Col span={6} key={module.key}>
                    <div style={{ 
                      padding: 12, 
                      border: '1px solid #d9d9d9', 
                      borderRadius: 6,
                      background: module.enabled ? '#f6ffed' : '#fff2f0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {module.enabled ? (
                          <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        ) : (
                          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                        )}
                        <span style={{ fontSize: 12, fontWeight: 'bold' }}>
                          {module.name}
                        </span>
                      </div>
                      <Tag color={module.enabled ? 'green' : 'red'} style={{ marginTop: 4, fontSize: '12px' }}>
                        {module.enabled ? '已啟用' : '已停用'}
                      </Tag>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Card>
      )}

      {/* 操作按鈕 */}
      <Card style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            {hasChanges && (
              <Alert
                message="配置已修改，請記得保存更改"
                type="warning"
                showIcon
                style={{ margin: 0 }}
              />
            )}
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadConfiguration}
                loading={loading}
              >
                重新載入
              </Button>
              <Button
                danger
                onClick={() => setResetModalVisible(true)}
                disabled={loading || saving}
              >
                重置為默認
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => form.submit()}
                loading={saving}
                disabled={!hasChanges}
              >
                保存配置
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 配置表單 */}
      <Form
        form={form}
        layout="vertical"
        onFinish={saveConfiguration}
        onValuesChange={handleFormChange}
      >
        <Tabs defaultActiveKey="airQuality">
          {/* 空氣品質配置 */}
          <TabPane 
            tab={
              <span>
                <ExperimentOutlined />
                空氣品質
              </span>
            } 
            key="airQuality"
          >
            <Card>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name={['airQuality', 'enabled']}
                    label="啟用空氣品質監控"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name={['airQuality', 'monitoringInterval']}
                    label="監控間隔 (分鐘)"
                    rules={[{ required: true, message: '請輸入監控間隔' }]}
                  >
                    <InputNumber min={1} max={60} style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    name={['airQuality', 'autoVentilation']}
                    label="自動通風控制"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Divider orientation="left">警報閾值設置</Divider>
                  
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name={['airQuality', 'alertThresholds', 'aqi', 'warning']}
                        label="AQI 警告值"
                      >
                        <InputNumber min={0} max={500} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={['airQuality', 'alertThresholds', 'aqi', 'critical']}
                        label="AQI 嚴重值"
                      >
                        <InputNumber min={0} max={500} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name={['airQuality', 'alertThresholds', 'temperature', 'min']}
                        label="溫度下限 (°C)"
                      >
                        <InputNumber min={-10} max={50} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={['airQuality', 'alertThresholds', 'temperature', 'max']}
                        label="溫度上限 (°C)"
                      >
                        <InputNumber min={-10} max={50} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name={['airQuality', 'alertThresholds', 'humidity', 'min']}
                        label="濕度下限 (%)"
                      >
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={['airQuality', 'alertThresholds', 'humidity', 'max']}
                        label="濕度上限 (%)"
                      >
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>
          </TabPane>

          {/* 智能照明配置 */}
          <TabPane 
            tab={
              <span>
                <ThunderboltOutlined />
                智能照明
              </span>
            } 
            key="lighting"
          >
            <Card>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name={['lighting', 'enabled']}
                    label="啟用智能照明"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name={['lighting', 'autoMode']}
                    label="自動模式"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name={['lighting', 'motionSensor']}
                    label="動作感應器"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name={['lighting', 'energySaving']}
                    label="節能模式"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Divider orientation="left">亮度範圍設置</Divider>
                  
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name={['lighting', 'brightnessRange', 'min']}
                        label="最低亮度 (%)"
                      >
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={['lighting', 'brightnessRange', 'max']}
                        label="最高亮度 (%)"
                      >
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider orientation="left">時間排程</Divider>
                  
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name={['lighting', 'schedule', 'workdays', 'start']}
                        label="工作日開始時間"
                      >
                        <Input placeholder="HH:MM" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={['lighting', 'schedule', 'workdays', 'end']}
                        label="工作日結束時間"
                      >
                        <Input placeholder="HH:MM" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name={['lighting', 'schedule', 'weekends', 'start']}
                        label="週末開始時間"
                      >
                        <Input placeholder="HH:MM" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={['lighting', 'schedule', 'weekends', 'end']}
                        label="週末結束時間"
                      >
                        <Input placeholder="HH:MM" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>
          </TabPane>

          {/* 噪音監控配置 */}
          <TabPane 
            tab={
              <span>
                <SoundOutlined />
                噪音監控
              </span>
            } 
            key="noise"
          >
            <Card>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name={['noise', 'enabled']}
                    label="啟用噪音監控"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name={['noise', 'monitoringInterval']}
                    label="監控間隔 (分鐘)"
                    rules={[{ required: true, message: '請輸入監控間隔' }]}
                  >
                    <InputNumber min={1} max={60} style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    name={['noise', 'autoControl']}
                    label="自動噪音控制"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Divider orientation="left">警報閾值設置</Divider>
                  
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name={['noise', 'alertThresholds', 'warning']}
                        label="警告閾值 (dB)"
                      >
                        <InputNumber min={30} max={120} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={['noise', 'alertThresholds', 'critical']}
                        label="嚴重閾值 (dB)"
                      >
                        <InputNumber min={30} max={120} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider orientation="left">安靜時段</Divider>
                  
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name={['noise', 'quietHours', 'start']}
                        label="開始時間"
                      >
                        <Input placeholder="HH:MM" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={['noise', 'quietHours', 'end']}
                        label="結束時間"
                      >
                        <Input placeholder="HH:MM" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>
          </TabPane>

          {/* 能源管理配置 */}
          <TabPane 
            tab={
              <span>
                <FireOutlined />
                能源管理
              </span>
            } 
            key="energy"
          >
            <Card>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name={['energy', 'enabled']}
                    label="啟用能源管理"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name={['energy', 'monitoringInterval']}
                    label="監控間隔 (分鐘)"
                    rules={[{ required: true, message: '請輸入監控間隔' }]}
                  >
                    <InputNumber min={1} max={60} style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    name={['energy', 'optimizationMode']}
                    label="優化模式"
                  >
                    <Select style={{ width: '100%' }}>
                      <Option value="balanced">平衡模式</Option>
                      <Option value="performance">性能優先</Option>
                      <Option value="energy_saving">節能優先</Option>
                      <Option value="cost_saving">成本優先</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name={['energy', 'autoOptimization']}
                    label="自動優化"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Divider orientation="left">警報閾值設置</Divider>
                  
                  <Form.Item
                    name={['energy', 'alertThresholds', 'dailyLimit']}
                    label="日消耗限制 (kWh)"
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    name={['energy', 'alertThresholds', 'monthlyLimit']}
                    label="月消耗限制 (kWh)"
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    name={['energy', 'alertThresholds', 'efficiencyMin']}
                    label="最低效率要求 (%)"
                  >
                    <InputNumber min={0} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </TabPane>

          {/* 系統設置 */}
          <TabPane 
            tab={
              <span>
                <SettingOutlined />
                系統設置
              </span>
            } 
            key="system"
          >
            <Card>
              <Row gutter={24}>
                <Col span={12}>
                  <Divider orientation="left">數據管理</Divider>
                  
                  <Form.Item
                    name={['system', 'dataRetention']}
                    label="數據保留期 (天)"
                    rules={[{ required: true, message: '請輸入數據保留期' }]}
                  >
                    <InputNumber min={1} max={365} style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    name={['system', 'backupInterval']}
                    label="備份間隔 (小時)"
                    rules={[{ required: true, message: '請輸入備份間隔' }]}
                  >
                    <InputNumber min={1} max={168} style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    name={['system', 'logLevel']}
                    label="日誌等級"
                  >
                    <Select style={{ width: '100%' }}>
                      <Option value="debug">調試</Option>
                      <Option value="info">信息</Option>
                      <Option value="warn">警告</Option>
                      <Option value="error">錯誤</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Divider orientation="left">通知設置</Divider>
                  
                  <Form.Item
                    name={['system', 'alertNotifications']}
                    label="系統警報通知"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name={['system', 'emailNotifications']}
                    label="郵件通知"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name={['system', 'smsNotifications']}
                    label="簡訊通知"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>

                  <Divider orientation="left">維護模式</Divider>
                  
                  <Form.Item
                    name={['system', 'maintenanceMode']}
                    label="維護模式"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </TabPane>
        </Tabs>
      </Form>

      {/* 重置確認模態框 */}
      <Modal
        title="確認重置配置"
        open={resetModalVisible}
        onOk={resetConfiguration}
        onCancel={() => setResetModalVisible(false)}
        confirmLoading={saving}
        okText="確認重置"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <div style={{ padding: '16px 0' }}>
          <Alert
            message="警告"
            description="此操作將重置所有系統配置為默認值，包括所有模組的設置、閾值和排程。此操作無法撤銷。"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <p>確定要重置所有配置嗎？</p>
        </div>
      </Modal>
    </div>
  );
};

export default SystemConfiguration;